// MUI Imports
import { useTheme } from '@mui/material/styles'

// Third-party Imports
import PerfectScrollbar from 'react-perfect-scrollbar'

// Type Imports
import type { VerticalMenuContextProps } from '@menu/components/vertical-menu/Menu'

// Component Imports
import { Menu, MenuItem } from '@menu/vertical-menu'

// Hook Imports
import useVerticalNav from '@menu/hooks/useVerticalNav'

// Styled Component Imports
import StyledVerticalNavExpandIcon from '@menu/styles/vertical/StyledVerticalNavExpandIcon'

// Style Imports
import menuItemStyles from '@core/styles/vertical/menuItemStyles'
import menuSectionStyles from '@core/styles/vertical/menuSectionStyles'
import PermissionCheckWrapper from '@/SDK/PermissionCheckWrapper'
import AccessLevel from '@/SDK/AccessLevel'

type RenderExpandIconProps = {
  open?: boolean
  transitionDuration?: VerticalMenuContextProps['transitionDuration']
}

type Props = {
  scrollMenu: (container: any, isPerfectScrollbar: boolean) => void
}

const RenderExpandIcon = ({ open, transitionDuration }: RenderExpandIconProps) => (
  <StyledVerticalNavExpandIcon open={open} transitionDuration={transitionDuration}>
    <i className='tabler-chevron-right' />
  </StyledVerticalNavExpandIcon>
)

const VerticalMenu = ({ scrollMenu }: Props) => {
  // Hooks
  const theme = useTheme()
  const verticalNavOptions = useVerticalNav()

  // Vars
  const { isBreakpointReached, transitionDuration } = verticalNavOptions

  const ScrollWrapper = isBreakpointReached ? 'div' : PerfectScrollbar

  return (
    // eslint-disable-next-line lines-around-comment
    /* Custom scrollbar instead of browser scroll, remove if you want browser scroll only */
    <ScrollWrapper
      {...(isBreakpointReached
        ? {
            className: 'bs-full overflow-y-auto overflow-x-hidden',
            onScroll: container => scrollMenu(container, false)
          }
        : {
            options: { wheelPropagation: false, suppressScrollX: true },
            onScrollY: container => scrollMenu(container, true)
          })}
    >
      {/* Incase you also want to scroll NavHeader to scroll with Vertical Menu, remove NavHeader from above and paste it below this comment */}
      {/* Vertical Menu */}
      <Menu
        popoutMenuOffset={{ mainAxis: 23 }}
        menuItemStyles={menuItemStyles(verticalNavOptions, theme)}
        renderExpandIcon={({ open }) => <RenderExpandIcon open={open} transitionDuration={transitionDuration} />}
        renderExpandedMenuItemIcon={{ icon: <i className='tabler-circle text-xs' /> }}
        menuSectionStyles={menuSectionStyles(verticalNavOptions, theme)}
      >
        {/*<PermissionCheckWrapper permissionName={'Pages.Dashboard'} minAccessLevel={AccessLevel.READ}>*/}
        <MenuItem href='/' icon={<i className='tabler-dashboard' />}>
          Dashboard
        </MenuItem>
        {/*</PermissionCheckWrapper>*/}

        <PermissionCheckWrapper permissionName={'Pages.Users'} minAccessLevel={AccessLevel.READ}>
          <MenuItem href='/users' icon={<i className='tabler-user' />} exactMatch={false} activeUrl='/users'>
            Users
          </MenuItem>
        </PermissionCheckWrapper>

        <PermissionCheckWrapper permissionName={'Pages.Teams'} minAccessLevel={AccessLevel.READ}>
          <MenuItem href='/teams' icon={<i className='tabler-users' />}>
            Teams
          </MenuItem>
        </PermissionCheckWrapper>

        <PermissionCheckWrapper permissionName={'Pages.Roles and Permissions'} minAccessLevel={AccessLevel.READ}>
          <MenuItem href='/roles' icon={<i className='tabler-circles' />}>
            Roles & Permissions
          </MenuItem>
        </PermissionCheckWrapper>

        <PermissionCheckWrapper permissionName={'Pages.Application'} minAccessLevel={AccessLevel.READ}>
          <MenuItem href='/apps' icon={<i className='tabler-apps' />}>
            Application
          </MenuItem>
        </PermissionCheckWrapper>

        <PermissionCheckWrapper permissionName={'Pages.Audit Logs'} minAccessLevel={AccessLevel.READ}>
          <MenuItem href='/audit' icon={<i className='tabler-file-isr' />}>
            Audit Logs
          </MenuItem>
        </PermissionCheckWrapper>

        <PermissionCheckWrapper permissionName={'Pages.Configuration'} minAccessLevel={AccessLevel.READ}>
          <MenuItem href='/config' icon={<i className='tabler-settings' />}>
            Configuration
          </MenuItem>
        </PermissionCheckWrapper>

        <MenuItem href='/example' icon={<i className='tabler-code' />}>
          Example
        </MenuItem>

        <PermissionCheckWrapper permissionName={'Pages.Roles and Permissions'} minAccessLevel={AccessLevel.READ}>
          <MenuItem href='/test' icon={<i className='tabler-flask' />}>
            Testing
          </MenuItem>
        </PermissionCheckWrapper>
      </Menu>
      {/* <Menu
        popoutMenuOffset={{ mainAxis: 23 }}
        menuItemStyles={menuItemStyles(verticalNavOptions, theme)}
        renderExpandIcon={({ open }) => <RenderExpandIcon open={open} transitionDuration={transitionDuration} />}
        renderExpandedMenuItemIcon={{ icon: <i className='tabler-circle text-xs' /> }}
        menuSectionStyles={menuSectionStyles(verticalNavOptions, theme)}
      >
        <GenerateVerticalMenu menuData={menuData(dictionary)} />
      </Menu> */}
    </ScrollWrapper>
  );
}

export default VerticalMenu
